/**
 * The API's error envelope, as a type.
 *
 * `app/errors.py` gives every failure the same shape, deliberately:
 *
 *     {"error": {"code": "invalid_credentials",
 *                "message": "That mobile number or email and password do not match.",
 *                "action": "Check your details and try again."}}
 *
 * So the admin app never maps a status code to English - it renders the
 * sentences the server sent. NFR-10.5.4 requires plain language with a next
 * action, and the server already wrote it; paraphrasing it here would be a
 * second copy free to drift.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly action: string | null;
  readonly field: string | null;

  constructor(
    status: number,
    code: string,
    message: string,
    action: string | null = null,
    field: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.action = action;
    this.field = field;
  }
}

/** The request never reached the API, or the API never answered. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkError";
  }
}

type Envelope = {
  error?: {
    code?: unknown;
    message?: unknown;
    action?: unknown;
    field?: unknown;
  };
};

const FALLBACK_MESSAGE = "Something went wrong.";
const FALLBACK_ACTION = "Try again. If it keeps happening, tell the front desk.";

/** Build an `ApiError` from a non-2xx response, whatever its body turned out to be. */
export async function toApiError(response: Response): Promise<ApiError> {
  let envelope: Envelope = {};
  try {
    envelope = (await response.json()) as Envelope;
  } catch {
    // A proxy, a 502 from the VPS, or a truncated body. The status is still
    // worth keeping, and the copy has to fall back to something honest.
  }

  const error = envelope.error ?? {};
  return new ApiError(
    response.status,
    typeof error.code === "string" ? error.code : "http_error",
    typeof error.message === "string" ? error.message : FALLBACK_MESSAGE,
    typeof error.action === "string" ? error.action : FALLBACK_ACTION,
    typeof error.field === "string" ? error.field : null,
  );
}

/**
 * The one thing the client does with an error beyond rendering it.
 *
 * `forbidden` is FR-8.1b's split, and it means something specific in a BROWSER:
 * the session is valid but belongs to a member, not an admin. The admin app can
 * only get into that state by a session being replaced under it, so it is worth
 * telling apart from an expired token - one needs "log in again", the other
 * needs "this account cannot use the admin panel at all".
 */
export function isSessionExpired(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.code === "not_authenticated" ||
      error.code === "invalid_refresh_token" ||
      error.status === 401)
  );
}

export function isWrongAccountKind(error: unknown): boolean {
  return error instanceof ApiError && error.code === "forbidden";
}

export function messageOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message;
  if (error instanceof Error) return error.message;
  return FALLBACK_MESSAGE;
}

export function actionOf(error: unknown): string | null {
  return error instanceof ApiError ? error.action : null;
}
